import { Flex, Text } from '@chakra-ui/react';
import { faEdit, faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonIcon,
  FormFeedback,
  Input,
  Modal,
  useToast
} from '@morressier/alembic';
import { useEffect, useRef, useState } from 'react';
import { Path } from 'react-hook-form';
import { useUpdateJournalSettings } from 'src/api/mutations';
import { FieldLabel } from 'src/components/Field';
import { JournalConfigForm } from '.';

type OptionItem = {
  id: string;
  value: string;
};

const MAX_OPTION_LENGTH = 75;

const EditJournalConfigFieldList = ({
  label,
  data,
  fieldName,
  journalId
}: {
  label: string;
  data: string[];
  fieldName: Path<JournalConfigForm>;
  journalId: string;
}) => {
  const toast = useToast();
  const updateSettingMutation = useUpdateJournalSettings(journalId);
  const idCounter = useRef(0);

  const nextId = () => `option-${idCounter.current++}`;

  const toOptionItems = (values: string[]): OptionItem[] =>
    (values ?? []).map(value => ({
      id: nextId(),
      value
    }));

  // Values are compared trimmed and case-insensitively, so "Reason" and
  // " reason " are treated as the same option.
  const getDuplicateIds = (items: OptionItem[]) => {
    const idsByValue = new Map<string, string[]>();

    items.forEach(option => {
      const key = option.value.trim().toLowerCase();
      if (!key) return;

      idsByValue.set(key, [...(idsByValue.get(key) ?? []), option.id]);
    });

    const duplicateIds = new Set<string>();
    idsByValue.forEach(ids => {
      if (ids.length > 1) ids.forEach(id => duplicateIds.add(id));
    });

    return duplicateIds;
  };

  // Refreshes duplicate markers across the list while keeping any
  // required/length errors already shown for individual fields.
  const withDuplicateErrors = (
    items: OptionItem[],
    baseErrors: Record<string, string>
  ) => {
    const next = { ...baseErrors };
    const duplicateIds = getDuplicateIds(items);

    items.forEach(option => {
      if (duplicateIds.has(option.id)) {
        next[option.id] = 'Duplicate option';
      } else if (next[option.id] === 'Duplicate option') {
        delete next[option.id];
      }
    });

    return next;
  };

  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<OptionItem[]>(() =>
    toOptionItems(data ?? [])
  );
  const [savedOptions, setSavedOptions] = useState<string[]>(data ?? []);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    setOptions(toOptionItems(data ?? []));
    setSavedOptions(data ?? []);
  }, [data]);

  const currentValues = () =>
    options.map(option => option.value.trim()).filter(Boolean);

  const openModal = () => {
    setOptions(toOptionItems(savedOptions));
    setErrors({});
    setListError(null);
    setIsOpen(true);
  };

  const onClose = () => setIsOpen(false);

  const addOption = () => {
    setOptions(prev => [...prev, { id: nextId(), value: '' }]);
    setListError(null);
  };

  const removeOption = (id: string) => {
    const nextOptions = options.filter(option => option.id !== id);
    setOptions(nextOptions);

    setErrors(prev => {
      const next = { ...prev };
      delete next[id];
      return withDuplicateErrors(nextOptions, next);
    });
  };

  const updateValue = (id: string, value: string) => {
    const nextOptions = options.map(option =>
      option.id === id ? { ...option, value } : option
    );
    setOptions(nextOptions);

    setErrors(prev => {
      const next = { ...prev };
      delete next[id];

      const merged = withDuplicateErrors(nextOptions, next);
      if (value.trim().length > MAX_OPTION_LENGTH) {
        merged[id] = `Must be ${MAX_OPTION_LENGTH} characters or fewer`;
      }

      return merged;
    });
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (options.length === 0) {
      setErrors(nextErrors);
      setListError('At least one option is required');
      return false;
    }

    const duplicateIds = getDuplicateIds(options);

    options.forEach(option => {
      const trimmed = option.value.trim();
      if (!trimmed) {
        nextErrors[option.id] = 'This field is required';
      } else if (trimmed.length > MAX_OPTION_LENGTH) {
        nextErrors[
          option.id
        ] = `Must be ${MAX_OPTION_LENGTH} characters or fewer`;
      } else if (duplicateIds.has(option.id)) {
        nextErrors[option.id] = 'Duplicate option';
      }
    });

    setErrors(nextErrors);
    setListError(
      duplicateIds.size > 0 ? 'Duplicate options are not allowed' : null
    );
    return Object.keys(nextErrors).length === 0;
  };

  const save = async () => {
    if (!validate()) return;

    const value = currentValues();

    const parts = fieldName.split('.');
    if (parts.length < 2) return;

    const nestedKey = parts[1] as string;
    const payload = {
      config: {
        [nestedKey]: value
      }
    };

    const res = await updateSettingMutation.mutateAsync({
      data: {},
      config: payload.config
    });

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Journal Config Edited',
        isClosable: true
      });
      setSavedOptions(value);
      setOptions(toOptionItems(value));
      setIsOpen(false);
      return;
    }

    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string'
          ? res?.message
          : 'Something went wrong',
      isClosable: true
    });
  };

  const isLoading = updateSettingMutation?.isLoading;

  return (
    <>
      <Flex direction="column">
        <Flex alignItems="center">
          <FieldLabel label={label} subText="" isRequired={false} />
          <ButtonIcon
            onClick={openModal}
            ariaLabel="edit-config"
            icon={<FontAwesomeIcon icon={faEdit} />}
          />
        </Flex>
        {savedOptions.length ? (
          <Flex direction="column" gap={1}>
            {savedOptions.map((value, index) => (
              <Flex key={index} alignItems="flex-start" gap={2}>
                <Text textStyle="textContext" flexShrink={0}>
                  {index + 1}.
                </Text>
                <Text
                  textStyle="textContext"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                >
                  {value}
                </Text>
              </Flex>
            ))}
          </Flex>
        ) : (
          <Text textStyle="textContext">No value provided</Text>
        )}
      </Flex>

      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          width={{ base: 'calc(100vw - 16px)', md: '42rem' } as any}
          closeButtonProps={{
            hidden: true
          }}
        >
          <Flex flexDirection="column" gap={4} padding={{ base: 4, md: 8 }}>
            <Flex fontSize={32}>
              <FontAwesomeIcon icon={faEdit} />
            </Flex>
            <Text textStyle="headingGroup">Edit {label}</Text>
            <Flex direction="column" gap={2}>
              {options.map((option, index) => (
                <Flex key={option.id} alignItems="flex-start" gap={2}>
                  <Text textStyle="textContext" flexShrink={0} mt={2}>
                    {index + 1}.
                  </Text>
                  <Flex direction="column" flex={1} gap={1}>
                    <Input
                      name={option.id}
                      value={option.value}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      isInvalid={!!errors[option.id]}
                      isDisabled={isLoading}
                      onChange={event =>
                        updateValue(option.id, event.target.value)
                      }
                      aria-label={`${label} option`}
                    />
                    {errors[option.id] && (
                      <FormFeedback variant="error">
                        {errors[option.id]}
                      </FormFeedback>
                    )}
                  </Flex>
                  <ButtonIcon
                    ariaLabel="delete-option"
                    icon={
                      <FontAwesomeIcon
                        icon={faTrashAlt}
                        style={{ color: 'red' }}
                      />
                    }
                    onClick={() => removeOption(option.id)}
                  />
                </Flex>
              ))}
            </Flex>
            {listError && (
              <FormFeedback variant="error">{listError}</FormFeedback>
            )}
            <Button
              variant="secondary"
              onClick={addOption}
              iconLeft={<FontAwesomeIcon icon={faPlus} />}
              alignSelf="flex-start"
            >
              Add New Option
            </Button>
            <Flex
              justifyContent="flex-end"
              gap={2}
              alignItems="center"
              marginTop={8}
            >
              <Button
                variant="secondary"
                onClick={onClose}
                isDisabled={isLoading}
              >
                Exit Without Saving
              </Button>
              <Button variant="primary" onClick={save} isLoading={isLoading}>
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </>
  );
};

export default EditJournalConfigFieldList;
